<template>
  <div class="component-testeditor">
    <!-- Header dell'editor -->
    <div class="editor-header">
      <v-row>
        <v-col cols="12">
          <div class="d-flex align-center">
            <!-- Toggle Modifica/Anteprima sulla sinistra -->
            <v-btn-toggle
              v-model="mode"
              mandatory
              borderless
              class="mr-4"
            >
              <v-btn value="preview" small>
                <v-icon left small>mdi-eye</v-icon>
                {{ $t('test.editor.mode.preview') }}
              </v-btn>
              <v-btn value="edit" small>
                <v-icon left small>mdi-pencil</v-icon>
                {{ $t('test.editor.mode.edit') }}
              </v-btn>
            </v-btn-toggle>

            <v-spacer></v-spacer>

            <!-- Informazioni sullo stato attuale -->
            <div class="mr-4">
              <span class="text--secondary text-caption">
                <v-icon small left :color="getStatusColor">{{ getStatusIcon }}</v-icon>
                {{ getStatusText }}
              </span>
            </div>

            <!-- Pulsante Salva Bozza -->
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  color="orange"
                  depressed
                  :dark="canSaveDraft && !savingDraft"
                  :loading="savingDraft"
                  :disabled="!canSaveDraft || savingDraft"
                  @click="saveDraft"
                  class="mr-4"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon left>mdi-content-save</v-icon>
                  {{ $t('test.editor.saveDraft.button') }}
                </v-btn>
              </template>
              <span v-if="!canSaveDraft">
                {{ $t('test.editor.saveDraft.tooltipNoChanges') }}
              </span>
              <span v-else-if="savingDraft">
                {{ $t('test.editor.saveDraft.tooltipSaving') }}
              </span>
              <span v-else>
                {{ $t('test.editor.saveDraft.tooltipActive') }}
              </span>
            </v-tooltip>

            <!-- Pulsante Pubblica sulla destra -->
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  color="success"
                  depressed
                  :loading="publishing"
                  :disabled="isPublishDisabled"
                  @click="showPublishDialog = true"
                  class="mr-4"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon left>mdi-publish</v-icon>
                  {{ $t('test.editor.publish.button') }}
                </v-btn>
              </template>
              <span v-if="!canPublish">
                {{ $t('test.editor.publish.tooltipNoChanges') }}
              </span>
              <span v-else-if="publishing">
                {{ $t('test.editor.publish.tooltipPublishing') }}
              </span>
              <span v-else>
                {{ $t('test.editor.publish.tooltipActive') }}
              </span>
            </v-tooltip>
          </div>
        </v-col>
      </v-row>
    </div>

    <!-- Contenuto principale -->
    <div class="editor-content">
      <v-row no-gutters class="fill-height" ref="editorRow">
        <!-- Prima colonna: Preview del test -->
        <v-col cols="8" class="pa-4">
          <div class="preview-container">
            <!-- Debug info -->
            <!-- v-alert v-if="localTest.length === 0" type="warning" class="mb-4">
              <strong>Debug:</strong> Nessun test caricato. localTest.length = {{ localTest.length }}
            </v-alert>

            <v-alert v-else type="info" class="mb-4">
              <strong>Debug:</strong> Test caricato con {{ localTest.length }} stage.
              Stage corrente: {{ previewStatus.currentStage }} - {{ currentStage ? currentStage.title : '' }}
            </v-alert -->

            <!-- Header degli stage -->
            <v-card outlined class="mb-4">
              <TestEditorStages
                v-model="previewStatus"
                :test="localTest"
                :mode="mode"
                :selected-stage-index="selectedStageIndex"
                :selected-question-index="selectedQuestionIndex"
                @stage-selected="onStageSelected"
              />
            </v-card>

            <!-- Contenuto dello stage corrente -->
            <v-card outlined class="mb-4">
              <TestEditorStage
                v-if="currentStage && currentStage.name !== 'stage4'"
                v-model="previewStatus"
                :stage="currentStage"
                :test="localTest"
                :mode="mode"
                :selected-question-index="selectedQuestionIndex"
                :selected-answer-index="selectedAnswerIndex"
                @question-selected="onQuestionSelected"
                @answer-selected="onAnswerSelected"
                @add-question="onAddQuestion"
                @remove-question="onRemoveQuestion"
                @add-answer="onAddAnswer"
                @remove-answer="onRemoveAnswer"
                @answers-reordered="onAnswersReorder"
              />

              <TestEditorProductSelection
                v-else-if="currentStage && currentStage.name === 'stage4'"
                v-model="previewStatus"
                :mode="mode"
                :test="localTest"
                :selected-item="selectedItem"
                :selected-item-type="selectedItemType"
                :phases="currentStage.phases || []"
                @config-updated="onProductSelectionConfigUpdate"
                @element-selected="onProductSelectionElementSelected"
              />
            </v-card>
          </div>
        </v-col>

        <!-- Seconda colonna: Pannello di configurazione -->
        <v-col cols="4" class="pa-4 grey lighten-5">
          <div class="config-panel" ref="configPanel" :style="{ marginTop: configPanelOffset + 'px' }">
            <!-- Sidebar per la modalità preview -->
            <template v-if="mode === 'preview'">
              <v-card outlined>
                <v-card-text class="text-center">
                  <v-icon large color="info" class="mb-2">mdi-eye</v-icon>
                  <h3>{{ $t('test.editor.previewPanel.title') }}</h3>
                  <p class="text--secondary">
                    {{ $t('test.editor.previewPanel.description') }}
                  </p>
                </v-card-text>
              </v-card>

              <!-- Stage 4: riepilogo selezioni per fase -->
              <TestSelectionSummary
                class="mt-4"
                v-if="currentStage && currentStage.name === 'stage4'"
                :phases="currentStage.phases || []"
                :phase-selections="previewStatus.stages.stage4.phases || {}"
              />
            </template>

            <!-- Pannello di configurazione per la modalità edit -->
            <template v-else>
              <TestEditorConfig
                v-if="selectedItem"
                :item="selectedItem"
                :item-type="selectedItemType"
                :test="localTest"
                :stage-index="selectedStageIndex"
                :question-index="selectedQuestionIndex"
                @update="onConfigUpdate"
                @delete="onConfigDelete"
              />

              <v-card v-else outlined>
                <v-card-text class="text-center">
                  <v-icon large color="grey" class="mb-2">mdi-cursor-default-click</v-icon>
                  <h4 class="text--secondary">{{ $t('test.editor.selectElement.title') }}</h4>
                  <p class="text--secondary mb-0">
                    {{ $t('test.editor.selectElement.description') }}
                  </p>
                </v-card-text>
              </v-card>
            </template>
          </div>
        </v-col>
      </v-row>
    </div>

    <!-- Dialog di conferma per rimozione domanda -->
    <v-dialog v-model="showRemoveQuestionDialog" max-width="500">
      <v-card>
        <v-card-title class="headline">
          <v-icon left color="error">mdi-delete</v-icon>
          {{ $t('test.editor.dialogs.removeQuestion.title') }}
        </v-card-title>

        <v-card-text>
          <v-alert
            type="warning"
            text
            class="mb-4"
          >
            <strong>{{ $t('test.editor.dialogs.shared.warningPrefix') }}</strong> {{ $t('test.editor.dialogs.removeQuestion.body') }}
          </v-alert>

          <p>
            <strong>{{ $t('test.editor.dialogs.shared.confirmProceed') }}</strong>
            {{ $t('test.editor.dialogs.shared.irreversible') }}
          </p>
        </v-card-text>

        <v-card-actions>
          <v-btn color="grey" text
            @click="showRemoveQuestionDialog = false">
            {{ $t('test.editor.dialogs.shared.cancel') }}
          </v-btn>

          <v-spacer></v-spacer>

          <v-btn color="error" text
            @click="confirmRemoveQuestion">
            {{ $t('test.editor.dialogs.shared.remove') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog di conferma per rimozione risposta -->
    <v-dialog v-model="showRemoveAnswerDialog" max-width="500">
      <v-card>
        <v-card-title class="headline">
          <v-icon left color="error">mdi-delete</v-icon>
          {{ $t('test.editor.dialogs.removeAnswer.title') }}
        </v-card-title>

        <v-card-text>
          <v-alert
            type="warning"
            text
            class="mb-4"
          >
            <strong>{{ $t('test.editor.dialogs.shared.warningPrefix') }}</strong> {{ $t('test.editor.dialogs.removeAnswer.body') }}
          </v-alert>

          <p>
            <strong>{{ $t('test.editor.dialogs.shared.confirmProceed') }}</strong>
            {{ $t('test.editor.dialogs.shared.irreversible') }}
          </p>
        </v-card-text>

        <v-card-actions>
          <v-btn color="grey" text
            @click="showRemoveAnswerDialog = false">
            {{ $t('test.editor.dialogs.shared.cancel') }}
          </v-btn>

          <v-spacer></v-spacer>

          <v-btn color="error" text
            @click="confirmRemoveAnswer">
            {{ $t('test.editor.dialogs.shared.remove') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog per aggiungere una nuova domanda -->
    <v-dialog v-model="showAddQuestionDialog" max-width="500">
      <v-card>
        <v-card-title>
          <v-icon left color="primary">mdi-plus</v-icon>
          {{ $t('test.editor.dialogs.addQuestion.title') }}
        </v-card-title>

        <v-card-text>
          <v-form ref="addQuestionForm" v-model="addQuestionFormValid" @submit.prevent="addQuestion">
            <v-text-field
              ref="questionTitleInput"
              v-model="newQuestionTitle"
              :label="$t('test.editor.dialogs.addQuestion.questionLabel')"
              :placeholder="$t('test.editor.dialogs.addQuestion.questionPlaceholder')"
              outlined
              dense
              autofocus
              :rules="questionTitleRules"
              @keyup.enter="addQuestion"
            ></v-text-field>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-btn color="grey" text @click="cancelAddQuestion">
            {{ $t('test.editor.dialogs.shared.cancel') }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            :disabled="!addQuestionFormValid"
            @click="addQuestion"
          >
            {{ $t('test.editor.dialogs.shared.add') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog per aggiungere una nuova risposta -->
    <v-dialog v-model="showAddAnswerDialog" max-width="500">
      <v-card>
        <v-card-title>
          <v-icon left color="primary">mdi-plus</v-icon>
          {{ $t('test.editor.dialogs.addAnswer.title') }}
        </v-card-title>

        <v-card-text>
          <v-form ref="addAnswerForm" v-model="addAnswerFormValid" @submit.prevent="addAnswer">
            <v-text-field
              ref="answerTitleInput"
              v-model="newAnswerTitle"
              :label="$t('test.editor.dialogs.addAnswer.answerLabel')"
              :placeholder="$t('test.editor.dialogs.addAnswer.answerPlaceholder')"
              outlined
              dense
              autofocus
              :rules="answerTitleRules"
              @keyup.enter="addAnswer"
            ></v-text-field>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-btn color="grey" text @click="cancelAddAnswer">
            {{ $t('test.editor.dialogs.shared.cancel') }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            :disabled="!addAnswerFormValid"
            @click="addAnswer"
          >
            {{ $t('test.editor.dialogs.shared.add') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- PublishTestDialog riutilizzato -->
    <PublishTestDialog
      v-model="showPublishDialog"
      @published="onPublished" />
  </div>
</template>

<script>
import { v4 as uuidv4 } from 'uuid';
import { mapActions } from 'vuex';
import TestEditorStages from '@/components/TestEditorStages';
import TestEditorStage from '@/components/TestEditorStage';
import TestEditorConfig from '@/components/TestEditorConfig';
import TestEditorProductSelection from '@/components/TestEditorProductSelection';
import PublishTestDialog from '@/components/PublishTestDialog';
import TestSelectionSummary from '@/components/TestSelectionSummary';

export default {
  name: 'TestEditor',

  components: {
    TestEditorStages,
    TestEditorStage,
    TestEditorConfig,
    TestEditorProductSelection,
    PublishTestDialog,
    TestSelectionSummary,
  },

  props: {
    test: {
      type: Array,
      required: true,
    },
    hasUnsavedChanges: {
      type: Boolean,
      default: false,
    },
    canSaveDraft: {
      type: Boolean,
      default: false,
    },
    canPublish: {
      type: Boolean,
      default: false,
    },
    isDraft: {
      type: Boolean,
      default: false,
    },
    hasChangesFromPublished: {
      type: Boolean,
      default: false,
    },
    lastPublishTime: {
      type: Date,
      default: null,
    },
  },

  data: () => ({
    localTest: [],
    mode: 'edit', // 'preview' o 'edit'
    publishing: false,
    savingDraft: false,
    autoSaving: false,
    lastAutoSave: null,

    // Dialog di conferma
    showPublishDialog: false,
    showRemoveQuestionDialog: false,
    showRemoveAnswerDialog: false,
    pendingRemoveQuestion: null,
    pendingRemoveAnswer: null,

    // Dialog per aggiungere domande e risposte
    showAddQuestionDialog: false,
    showAddAnswerDialog: false,
    newQuestionTitle: '',
    newAnswerTitle: '',
    addQuestionFormValid: false,
    addAnswerFormValid: false,
    pendingAddQuestion: null, // { stageIndex }
    pendingAddAnswer: null, // { stageIndex, questionIndex }
    configPanelOffset: 0,

    // Stato della preview (simile a Test.vue)
    previewStatus: {
      currentStage: 1,
      currentStep: 1,
      colorlist: null,
      color: null,
      complete: false,
      stages: {
        stage1: { complete: false, questions: {} },
        stage2: { complete: false, questions: {} },
        stage3: { complete: false, questions: {} },
        stage4: { complete: false, phases: {} },
      },
    },

    // Elemento selezionato per la configurazione
    selectedItem: null,
    selectedItemType: null, // 'stage', 'question', 'answer', 'phase', 'choice'
    selectedStageIndex: null,
    selectedQuestionIndex: null,
    selectedAnswerIndex: null,
    selectedPhaseIndex: null,
    selectedChoiceIndex: null,

  }),

  created() {
    // eslint-disable-next-line no-console
    console.log('TestEditor: component created');
    // eslint-disable-next-line no-console
    console.log('TestEditor: initial test prop:', this.test);
  },

  computed: {
    currentStage() {
      return this.localTest[this.previewStatus.currentStage - 1] || null;
    },

    // Debug computed properties
    debugInfo() {
      return {
        localTestLength: this.localTest.length,
        localTest: this.localTest,
        currentStageIndex: this.previewStatus.currentStage - 1,
        currentStage: this.currentStage,
      };
    },

    // Controlla se il pulsante Pubblica deve essere disabilitato
    isPublishDisabled() {
      return !this.canPublish || this.publishing;
    },

    questionTitleRules() {
      return [
        (v) => !!v || this.$t('test.editor.validation.questionTitleRequired'),
        (v) => (v && v.length >= 3) || this.$t('test.editor.validation.questionTitleMinLength'),
        (v) => (v && v.length <= 200) || this.$t('test.editor.validation.questionTitleMaxLength'),
      ];
    },

    answerTitleRules() {
      return [
        (v) => !!v || this.$t('test.editor.validation.answerTitleRequired'),
        (v) => (v && v.length >= 1) || this.$t('test.editor.validation.answerTitleMinLength'),
        (v) => (v && v.length <= 100) || this.$t('test.editor.validation.answerTitleMaxLength'),
      ];
    },

    getStatusIcon() {
      if (this.autoSaving) {
        return 'mdi-content-save-outline';
      }
      if (this.isDraft) {
        if (this.hasChangesFromPublished) {
          return 'mdi-file-document-edit';
        }
        return 'mdi-file-document';
      }
      if (this.hasChangesFromPublished) {
        return 'mdi-pencil';
      }
      return 'mdi-check-circle';
    },

    getStatusColor() {
      if (this.autoSaving) {
        return 'orange';
      }
      if (this.isDraft) {
        if (this.hasChangesFromPublished) {
          return 'orange';
        }
        return 'blue';
      }
      if (this.hasChangesFromPublished) {
        return 'orange';
      }
      return 'success';
    },

    getStatusText() {
      if (this.autoSaving) {
        return this.$t('test.editor.status.autoSaving');
      }
      if (this.isDraft) {
        if (this.hasChangesFromPublished) {
          let text = this.$t('test.editor.status.draftWithChanges');
          if (this.lastAutoSave) {
            text += ` ${this.$t('test.editor.status.autoSavedSuffix')}`;
          }
          return text;
        }
        let text = this.$t('test.editor.status.draftIdentical');
        if (this.lastAutoSave) {
          text += ` ${this.$t('test.editor.status.autoSavedSuffix')}`;
        }
        return text;
      }
      if (this.hasChangesFromPublished) {
        return this.$t('test.editor.status.unpublishedChanges');
      }
      return this.$t('test.editor.status.published');
    },

  },

  watch: {
    test: {
      handler(newTest) {
        // eslint-disable-next-line no-console
        console.log('TestEditor: test prop changed:', newTest);
        this.localTest = this.ensureFixedStructure(newTest || []);
        // eslint-disable-next-line no-console
        console.log('TestEditor: localTest updated:', this.localTest);
      },
      immediate: true,
      deep: true,
    },

    mode(newMode, oldMode) {
      if (newMode === 'preview') {
        // Salvataggio automatico se ci sono modifiche non salvate
        this.autoSaveDraft();

        // Reset selezione quando si passa alla preview
        this.selectedItem = null;
        this.selectedItemType = null;
        this.configPanelOffset = 0;

        // Reset completo del previewStatus come se fosse premuto "Ricomincia"
        this.previewStatus.currentStep = 1;
        this.previewStatus.currentStage = 1;

        // Reset di tutti gli stage
        const allStages = ['stage1', 'stage2', 'stage3', 'stage4'];
        allStages.forEach((stageName) => {
          if (this.previewStatus.stages[stageName]) {
            this.$set(this.previewStatus.stages[stageName], 'questions', {});
            if (stageName === 'stage4') {
              this.$set(this.previewStatus.stages[stageName], 'phases', {});
            }
            this.previewStatus.stages[stageName].complete = false;
          }
        });

        this.previewStatus.complete = false;
      } else if (newMode === 'edit' && oldMode === 'preview') {
        // Quando si passa da preview a edit, mantieni la domanda corrente
        this.preserveCurrentQuestionOnModeSwitch();

        // Salvataggio automatico se ci sono modifiche non salvate
        this.autoSaveDraft();
      }
    },

  },

  beforeDestroy() {
    if (this.offsetTimer) {
      clearTimeout(this.offsetTimer);
    }
  },

  mounted() {
    // Fallback se non ci sono dati
    if (!this.test || this.test.length === 0) {
      // eslint-disable-next-line no-console
      console.log('TestEditor: No test data provided, using fallback');
      const fallbackTest = [
        {
          name: 'stage1',
          title: 'Tipologia del supporto',
          subtitle: 'Scegli il tipo di superficie da trattare',
          questions: [
            {
              id: 'q1',
              title: 'È all\'interno o all\'esterno?',
              type: 'buttons',
              visibility: 'always_visible',
              conditions: [],
              answers: [
                {
                  id: 'a1',
                  title: 'Interno',
                  visibility: 'always_visible',
                  conditions: [],
                },
                {
                  id: 'a2',
                  title: 'Esterno',
                  visibility: 'always_visible',
                  conditions: [],
                },
              ],
            },
            {
              id: 'q2',
              title: 'Che tipo di supporto?',
              type: 'buttons',
              visibility: 'always_visible',
              conditions: [],
              answers: [
                {
                  id: 'a3',
                  title: 'Blocchi in laterizio',
                  visibility: 'always_visible',
                  conditions: [],
                },
                {
                  id: 'a4',
                  title: 'Cartongesso',
                  visibility: 'always_visible',
                  conditions: [],
                  hidden: [
                    {
                      questionId: 'q1',
                      answerId: 'a2',
                    },
                  ],
                },
              ],
            },
            {
              id: 'q3',
              name: 'tipo-supporto',
              title: 'Che tipo di supporto?',
              type: 'buttons',
              visibility: 'always_hidden',
              conditions: [
                {
                  questionId: 'q2',
                  answerId: 'a3',
                },
              ],
              answers: [
                {
                  id: 'a5',
                  title: 'Intonaco minerale',
                  visibility: 'always_visible',
                  conditions: [],
                },
                {
                  id: 'a6',
                  title: 'Intonaco di puro gesso',
                  visibility: 'always_visible',
                  conditions: [
                    {
                      questionId: 'q1',
                      answerId: 'a2',
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          name: 'stage2',
          title: 'Analisi del supporto',
          subtitle: 'Analizza le condizioni della superficie',
          questions: [],
        },
        {
          name: 'stage3',
          title: 'Tipologia di finitura',
          subtitle: 'Scegli il tipo di finitura desiderata',
          questions: [],
        },
        {
          name: 'stage4',
          title: 'Scelta dei prodotti',
          subtitle: 'Seleziona i prodotti necessari',
          phases: [],
        },
      ];
      this.localTest = this.ensureFixedStructure(fallbackTest);
    }
  },

  methods: {
    ...mapActions('wizards', {
      publishTestAction: 'publishWizard',
      saveDraftAction: 'saveDraft',
    }),

    ensureFixedStructure(testConfig) {
      // eslint-disable-next-line no-console
      console.log('TestEditor: ensureFixedStructure called with:', testConfig);

      const fixedStages = [
        {
          name: 'stage1',
          title: 'Tipologia del supporto',
          subtitle: 'Scegli il tipo di superficie da trattare',
          questions: [],
        },
        {
          name: 'stage2',
          title: 'Analisi del supporto',
          subtitle: 'Analizza le condizioni della superficie',
          questions: [],
        },
        {
          name: 'stage3',
          title: 'Tipologia di finitura',
          subtitle: 'Scegli il tipo di finitura desiderata',
          questions: [],
        },
        {
          name: 'stage4',
          title: 'Scelta dei prodotti',
          subtitle: 'Seleziona i prodotti necessari',
          phases: [],
        },
      ];

      const result = fixedStages.map((fixedStage, index) => {
        const existingStage = testConfig[index];
        if (existingStage) {
          const stage = {
            ...fixedStage,
            title: existingStage.title || fixedStage.title,
            subtitle: existingStage.subtitle || fixedStage.subtitle,
            questions: existingStage.questions || fixedStage.questions,
            phases: existingStage.phases || fixedStage.phases,
          };

          if (stage.questions) {
            stage.questions.forEach((question, questionIndex) => {
              const updatedQuestion = { ...question };

              if (!updatedQuestion.id) {
                updatedQuestion.id = uuidv4();
              }

              if (!updatedQuestion.visibility) {
                updatedQuestion.visibility = 'always_visible';
              }
              if (!updatedQuestion.conditions) {
                updatedQuestion.conditions = [];
              }

              if (updatedQuestion.answers) {
                const updatedAnswers = updatedQuestion.answers.map((answer) => {
                  const updatedAnswer = { ...answer };

                  if (!updatedAnswer.id) {
                    updatedAnswer.id = uuidv4();
                  }

                  if (!answer.visibility) {
                    updatedAnswer.visibility = 'always_visible';
                  }
                  if (!answer.conditions) {
                    updatedAnswer.conditions = [];
                  }
                  return updatedAnswer;
                });

                updatedQuestion.answers = updatedAnswers;
              }

              stage.questions[questionIndex] = updatedQuestion;
            });
          }

          // Gestione delle fasi per lo stage4
          if (stage.phases) {
            stage.phases.forEach((phase, phaseIndex) => {
              const updatedPhase = { ...phase };

              if (!updatedPhase.id) {
                updatedPhase.id = `phase_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
              }

              if (!updatedPhase.visibility) {
                updatedPhase.visibility = 'always_visible';
              }
              if (!updatedPhase.conditions) {
                updatedPhase.conditions = [];
              }
              if (!updatedPhase.subtitle) {
                updatedPhase.subtitle = null;
              }
              if (updatedPhase.procedure === undefined) {
                updatedPhase.procedure = null;
              }
              if (updatedPhase.choices === undefined) {
                updatedPhase.choices = [];
              }

              // Gestione delle scelte per ogni fase
              if (updatedPhase.choices) {
                updatedPhase.choices.forEach((choice, choiceIndex) => {
                  const updatedChoice = { ...choice };

                  if (!updatedChoice.id) {
                    updatedChoice.id = `choice_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                  }
                  if (!updatedChoice.visibility) {
                    updatedChoice.visibility = 'always_visible';
                  }
                  if (!updatedChoice.conditions) {
                    updatedChoice.conditions = [];
                  }
                  if (!updatedChoice.recommended) {
                    updatedChoice.recommended = 'never';
                  }
                  if (!updatedChoice.recommendedConditions) {
                    updatedChoice.recommendedConditions = [];
                  }
                  if (updatedChoice.procedure === undefined) {
                    updatedChoice.procedure = null;
                  }
                  if (updatedChoice.withProducts === undefined) {
                    updatedChoice.withProducts = false;
                  }
                  if (!updatedChoice.products) {
                    updatedChoice.products = [];
                  }

                  updatedPhase.choices[choiceIndex] = updatedChoice;
                });
              }

              stage.phases[phaseIndex] = updatedPhase;
            });
          }

          return stage;
        }
        return fixedStage;
      });

      // eslint-disable-next-line no-console
      console.log('TestEditor: ensureFixedStructure result:', result);
      return result;
    },

    // Gestione selezione elementi
    onStageSelected(stageIndex) {
      if (this.mode === 'edit') {
        // Salvataggio automatico se ci sono modifiche non salvate
        this.autoSaveDraft();

        this.selectedItem = this.localTest[stageIndex];
        this.selectedItemType = 'stage';
        this.selectedStageIndex = stageIndex;
        this.selectedQuestionIndex = null;
        this.selectedAnswerIndex = null;
        this.selectedPhaseIndex = null;

        // Aggiorna anche previewStatus.currentStage per mostrare lo stage selezionato
        this.previewStatus.currentStage = stageIndex + 1;
        this.previewStatus.currentStep = 1;
        this.configPanelOffset = 0;
      }
    },

    onQuestionSelected(stageIndex, questionIndex) {
      if (this.mode === 'edit') {
        // Salvataggio automatico se ci sono modifiche non salvate
        this.autoSaveDraft();

        this.selectedItem = this.localTest[stageIndex].questions[questionIndex];
        this.selectedItemType = 'question';
        this.selectedStageIndex = stageIndex;
        this.selectedQuestionIndex = questionIndex;
        this.selectedAnswerIndex = null;
        this.selectedPhaseIndex = null;

        // Aggiorna currentStep per espandere la domanda selezionata
        this.previewStatus.currentStep = questionIndex + 1;
        this.updateConfigPanelOffset();
      }
    },

    onAnswerSelected(stageIndex, questionIndex, answerIndex) {
      if (this.mode === 'edit') {
        // Salvataggio automatico se ci sono modifiche non salvate
        this.autoSaveDraft();

        this.selectedItem = this.localTest[stageIndex].questions[questionIndex]
          .answers[answerIndex];
        this.selectedItemType = 'answer';
        this.selectedStageIndex = stageIndex;
        this.selectedQuestionIndex = questionIndex; // Mantiene l'indice per il rendering
        this.selectedAnswerIndex = answerIndex;
        this.selectedPhaseIndex = null;
      }
    },

    onAddQuestion(stageIndex) {
      this.pendingAddQuestion = { stageIndex };
      this.newQuestionTitle = '';
      this.addQuestionFormValid = false;
      this.showAddQuestionDialog = true;

      // Reset della validazione del form nel nextTick per assicurarsi che il dialog sia aperto
      this.$nextTick(() => {
        if (this.$refs.addQuestionForm) {
          this.$refs.addQuestionForm.resetValidation();
        }
      });
    },

    onRemoveQuestion(stageIndex, questionIndex) {
      this.pendingRemoveQuestion = { stageIndex, questionIndex };
      this.showRemoveQuestionDialog = true;
    },

    onAddAnswer(stageIndex, questionIndex) {
      this.pendingAddAnswer = { stageIndex, questionIndex };
      this.newAnswerTitle = '';
      this.addAnswerFormValid = false;
      this.showAddAnswerDialog = true;

      // Reset della validazione del form nel nextTick per assicurarsi che il dialog sia aperto
      this.$nextTick(() => {
        if (this.$refs.addAnswerForm) {
          this.$refs.addAnswerForm.resetValidation();
        }
      });
    },

    onRemoveAnswer(stageIndex, questionIndex, answerIndex) {
      this.pendingRemoveAnswer = { stageIndex, questionIndex, answerIndex };
      this.showRemoveAnswerDialog = true;
    },

    // Gestione aggiornamenti configurazione
    onConfigUpdate(updatedItem, additionalData = {}) {
      if (this.selectedItemType === 'stage') {
        this.$set(this.localTest, this.selectedStageIndex, updatedItem);
      } else if (this.selectedItemType === 'question') {
        this.$set(
          this.localTest[this.selectedStageIndex].questions,
          this.selectedQuestionIndex,
          updatedItem,
        );
      } else if (this.selectedItemType === 'answer') {
        this.$set(
          this.localTest[this.selectedStageIndex].questions[this.selectedQuestionIndex]
            .answers,
          this.selectedAnswerIndex,
          updatedItem,
        );
      } else if (this.selectedItemType === 'phase') {
        // Per le fasi, aggiorniamo l'elemento selezionato e la configurazione dello stage4
        this.selectedItem = updatedItem;

        // Aggiorna la fase nello stage4 usando l'indice selezionato
        const stage4Index = 3; // Stage 4 è all'indice 3
        if (this.localTest[stage4Index] && this.localTest[stage4Index].phases && this.selectedPhaseIndex !== null) {
          this.$set(this.localTest[stage4Index].phases, this.selectedPhaseIndex, updatedItem);
        }
      } else if (this.selectedItemType === 'choice') {
        // Per le scelte, aggiorniamo l'elemento selezionato e la configurazione della fase
        this.selectedItem = updatedItem;

        // Aggiorna la scelta nella fase usando gli indici selezionati
        const stage4Index = 3; // Stage 4 è all'indice 3
        if (this.localTest[stage4Index] && this.localTest[stage4Index].phases && this.selectedPhaseIndex !== null && this.selectedChoiceIndex !== null) {
          const phase = this.localTest[stage4Index].phases[this.selectedPhaseIndex];
          if (phase && phase.choices) {
            this.$set(phase.choices, this.selectedChoiceIndex, updatedItem);
          }
        }
      }

      // Gestione operazioni speciali
      if (additionalData.action === 'add_question') {
        this.addQuestion(this.selectedStageIndex);
      } else if (additionalData.action === 'add_answer') {
        this.addAnswer(this.selectedStageIndex, this.selectedQuestionIndex);
      }

      this.emitChange();
    },

    onConfigDelete() {
      if (this.selectedItemType === 'question') {
        this.removeQuestion(this.selectedStageIndex, this.selectedQuestionIndex);
      } else if (this.selectedItemType === 'answer') {
        this.removeAnswer(
          this.selectedStageIndex,
          this.selectedQuestionIndex,
          this.selectedAnswerIndex,
        );
      } else if (this.selectedItemType === 'phase') {
        this.removePhase(this.selectedStageIndex, this.selectedPhaseIndex);
      } else if (this.selectedItemType === 'choice') {
        this.removeChoice(this.selectedStageIndex, this.selectedPhaseIndex, this.selectedChoiceIndex);
      }
    },

    // Metodi per aggiungere/rimuovere elementi
    addQuestion() {
      if (!this.addQuestionFormValid || !this.newQuestionTitle.trim() || !this.pendingAddQuestion) {
        return;
      }

      const { stageIndex } = this.pendingAddQuestion;
      const stage = this.localTest[stageIndex];
      if (!stage.questions) {
        this.$set(stage, 'questions', []);
      }

      const newQuestion = {
        id: uuidv4(),
        title: this.newQuestionTitle.trim(),
        type: 'buttons',
        answers: [],
        visibility: 'always_visible',
        conditions: [],
      };

      stage.questions.push(newQuestion);

      this.$nextTick(() => {
        const newQuestionIndex = stage.questions.length - 1;
        this.onQuestionSelected(stageIndex, newQuestionIndex);
      });

      this.emitChange();
      this.cancelAddQuestion();
    },

    confirmRemoveQuestion() {
      if (this.pendingRemoveQuestion) {
        const { stageIndex, questionIndex } = this.pendingRemoveQuestion;
        this.localTest[stageIndex].questions.splice(questionIndex, 1);
        this.selectedItem = null;
        this.selectedItemType = null;
        this.emitChange();
        this.pendingRemoveQuestion = null;
      }
      this.showRemoveQuestionDialog = false;
    },

    addAnswer() {
      if (!this.addAnswerFormValid || !this.newAnswerTitle.trim() || !this.pendingAddAnswer) {
        return;
      }

      const { stageIndex, questionIndex } = this.pendingAddAnswer;
      const question = this.localTest[stageIndex].questions[questionIndex];
      if (!question.answers) {
        this.$set(question, 'answers', []);
      }

      const newAnswer = {
        id: uuidv4(),
        title: this.newAnswerTitle.trim(),
        visibility: 'always_visible',
        conditions: [],
      };

      question.answers.push(newAnswer);

      // Seleziona automaticamente la nuova risposta
      const newAnswerIndex = question.answers.length - 1;
      this.selectedItem = newAnswer;
      this.selectedItemType = 'answer';
      this.selectedStageIndex = stageIndex;
      this.selectedQuestionIndex = questionIndex;
      this.selectedAnswerIndex = newAnswerIndex;

      this.emitChange();
      this.cancelAddAnswer();
    },

    /**
     * Annulla l'aggiunta di una nuova domanda
     */
    cancelAddQuestion() {
      this.showAddQuestionDialog = false;
      this.newQuestionTitle = '';
      this.addQuestionFormValid = false;
      this.pendingAddQuestion = null;

      // Reset della validazione del form
      if (this.$refs.addQuestionForm) {
        this.$refs.addQuestionForm.resetValidation();
      }
    },

    /**
     * Annulla l'aggiunta di una nuova risposta
     */
    cancelAddAnswer() {
      this.showAddAnswerDialog = false;
      this.newAnswerTitle = '';
      this.addAnswerFormValid = false;
      this.pendingAddAnswer = null;

      // Reset della validazione del form
      if (this.$refs.addAnswerForm) {
        this.$refs.addAnswerForm.resetValidation();
      }
    },

    confirmRemoveAnswer() {
      if (this.pendingRemoveAnswer) {
        const { stageIndex, questionIndex, answerIndex } = this.pendingRemoveAnswer;
        this.localTest[stageIndex].questions[questionIndex].answers.splice(answerIndex, 1);
        this.selectedItem = null;
        this.selectedItemType = null;
        this.emitChange();
        this.pendingRemoveAnswer = null;
      }
      this.showRemoveAnswerDialog = false;
    },

    onAnswersReorder() {
      // Il riordinamento è già stato applicato automaticamente dal v-model di draggable
      // Questo metodo serve solo per emettere l'evento di cambio
      this.emitChange();
    },

    removePhase(stageIndex, phaseIndex) {
      if (stageIndex === 3 && phaseIndex !== null && this.localTest[stageIndex].phases) {
        this.localTest[stageIndex].phases.splice(phaseIndex, 1);
        this.selectedItem = null;
        this.selectedItemType = null;
        this.selectedPhaseIndex = null;
        this.selectedChoiceIndex = null;
        this.emitChange();
      }
    },

    removeChoice(stageIndex, phaseIndex, choiceIndex) {
      if (stageIndex === 3 && phaseIndex !== null && choiceIndex !== null && this.localTest[stageIndex].phases) {
        const phase = this.localTest[stageIndex].phases[phaseIndex];
        if (phase && phase.choices) {
          phase.choices.splice(choiceIndex, 1);
          this.selectedItem = null;
          this.selectedItemType = null;
          this.selectedChoiceIndex = null;
          this.emitChange();
        }
      }
    },

    emitChange() {
      this.$emit('input', JSON.parse(JSON.stringify(this.localTest)));
    },

    onPublished() {
      // Emetti evento per notificare il componente padre
      this.$emit('published');
    },

    async saveDraft() {
      try {
        this.savingDraft = true;
        await this.saveDraftAction();
        this.$emit('draft-saved');
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Errore durante il salvataggio della bozza:', error);
        // TODO: Mostrare un messaggio di errore all'utente
      } finally {
        this.savingDraft = false;
      }
    },

    async autoSaveDraft() {
      // Salva automaticamente solo se ci sono modifiche non salvate
      if (!this.canSaveDraft || this.autoSaving) {
        return;
      }

      try {
        this.autoSaving = true;
        await this.saveDraftAction();
        this.lastAutoSave = new Date();
        // Emetti evento per aggiornare originalConfig nel componente padre
        this.$emit('draft-saved');
        // eslint-disable-next-line no-console
        console.log('Salvataggio automatico completato');
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Errore durante il salvataggio automatico:', error);
        // Non mostrare errori all'utente per il salvataggio automatico
      } finally {
        this.autoSaving = false;
      }
    },

    formatDate(date) {
      if (!date) return '';
      const localeMap = { it: 'it-IT', en: 'en-GB' };
      const intlLocale = localeMap[this.$i18n.locale] || 'it-IT';
      return new Intl.DateTimeFormat(intlLocale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    },

    formatTimeAgo(date) {
      if (!date) return '';
      const now = new Date();
      const diffInSeconds = Math.floor((now - date) / 1000);

      if (diffInSeconds < 60) {
        return this.$t('test.editor.status.timeAgo.secondsAgo', { n: diffInSeconds });
      }
      if (diffInSeconds < 3600) {
        return this.$t('test.editor.status.timeAgo.minutesAgo', { n: Math.floor(diffInSeconds / 60) });
      }
      if (diffInSeconds < 86400) {
        return this.$t('test.editor.status.timeAgo.hoursAgo', { n: Math.floor(diffInSeconds / 3600) });
      }
      return this.$t('test.editor.status.timeAgo.daysAgo', { n: Math.floor(diffInSeconds / 86400) });
    },

    preserveCurrentQuestionOnModeSwitch() {
      // Identifica lo stage corrente e la domanda visualizzata in preview
      const currentStageIndex = this.previewStatus.currentStage - 1;
      const currentQuestionStep = this.previewStatus.currentStep - 1; // Step basato su 1, quindi sottrai 1 per l'indice

      if (!this.currentStage || !this.currentStage.questions || currentQuestionStep < 0) {
        return;
      }

      // Ottieni le domande filtrate che erano visibili in preview
      const visibleQuestionsInPreview = this.getVisibleQuestionsForStage(this.currentStage, true);

      if (currentQuestionStep >= visibleQuestionsInPreview.length) {
        return;
      }

      // Identifica la domanda specifica che era visualizzata
      const targetQuestion = visibleQuestionsInPreview[currentQuestionStep];
      if (!targetQuestion || !targetQuestion.id) {
        return;
      }

      // Trova l'indice di questa domanda nell'array completo (non filtrato) dello stage
      const allQuestionsInStage = this.currentStage.questions || [];
      const targetQuestionIndexInFullArray = allQuestionsInStage.findIndex((q) => q.id === targetQuestion.id);

      console.log('DEBUG preserveCurrentQuestionOnModeSwitch:', {
        currentQuestionStep,
        targetQuestionId: targetQuestion.id,
        targetQuestionTitle: targetQuestion.title,
        targetQuestionIndexInFullArray,
        totalQuestionsInStage: allQuestionsInStage.length,
        visibleQuestionsInPreviewLength: visibleQuestionsInPreview.length,
      });

      if (targetQuestionIndexInFullArray !== -1) {
        // Imposta la selezione sulla domanda corretta in modalità edit
        this.selectedItem = targetQuestion;
        this.selectedItemType = 'question';
        this.selectedStageIndex = currentStageIndex;
        this.selectedQuestionIndex = targetQuestionIndexInFullArray;
        this.selectedAnswerIndex = null;
        this.selectedPhaseIndex = null;

        // Reset temporaneo per forzare l'aggiornamento del stepper
        this.previewStatus.currentStep = 0;

        console.log('DEBUG: Resetting currentStep to 0, then setting to:', targetQuestionIndexInFullArray + 1);

        // Nell'nextTick, imposta il currentStep corretto per la modalità edit
        this.$nextTick(() => {
          // In modalità edit, il currentStep deve corrispondere alla posizione
          // della domanda nell'array completo + 1 (perché il stepper è basato su 1)
          this.previewStatus.currentStep = targetQuestionIndexInFullArray + 1;
          console.log('DEBUG: currentStep set to:', this.previewStatus.currentStep);
        });
      }
    },

    getVisibleQuestionsForStage(stage, isPreviewMode = false) {
      if (!stage || !Array.isArray(stage.questions)) {
        return [];
      }

      if (!isPreviewMode) {
        // In modalità edit, tutte le domande sono visibili
        return stage.questions;
      }

      // In modalità preview, filtra le domande usando la stessa logica di TestEditorStage
      return stage.questions.filter((question) => this.isQuestionVisibleInPreview(question));
    },

    isQuestionVisibleInPreview(question) {
      // Replica la logica di isQuestionVisible da TestEditorStage.vue
      if (!question.conditions || question.conditions.length === 0) {
        return true;
      }

      const conditionsSatisfied = this.evaluateConditionsForQuestion(question.conditions);

      if (question.visibility === 'always_visible') {
        return !conditionsSatisfied;
      }
      if (question.visibility === 'always_hidden') {
        return conditionsSatisfied;
      }

      return true;
    },

    evaluateConditionsForQuestion(conditions) {
      if (!conditions || conditions.length === 0) {
        return false;
      }

      return conditions.some((condition) => this.isConditionSatisfiedForQuestion(condition));
    },

    isConditionSatisfiedForQuestion(condition) {
      // Normalizza: supporta sia answerIds (nuovo) che answerId (legacy)
      let conditionAnswerIds = [];
      if (Array.isArray(condition.answerIds) && condition.answerIds.length > 0) {
        conditionAnswerIds = condition.answerIds;
      } else if (condition.answerId) {
        conditionAnswerIds = [condition.answerId];
      }

      if (!condition.questionId || conditionAnswerIds.length === 0) {
        return false;
      }

      const allStages = ['stage1', 'stage2', 'stage3', 'stage4'];
      const currentStageIndex = allStages.indexOf(this.currentStage?.name);

      for (let i = 0; i <= currentStageIndex; i += 1) {
        const stageName = allStages[i];
        const stageStatus = this.previewStatus.stages[stageName];

        if (stageStatus && stageStatus.questions) {
          const questionResponse = stageStatus.questions[condition.questionId];
          if (questionResponse && questionResponse.complete) {
            // Checkbox: answerIds è un array — verifica intersezione
            if (Array.isArray(questionResponse.answerIds)) {
              return questionResponse.answerIds.some((id) => conditionAnswerIds.includes(id));
            }
            // Button/Radio: answerId è una stringa
            return conditionAnswerIds.includes(questionResponse.answerId);
          }
        }
      }

      return false;
    },

    // Metodi per gestire gli eventi del TestEditorProductSelection
    onProductSelectionConfigUpdate(configData) {
      console.log('TestEditor: onProductSelectionConfigUpdate called with:', configData);

      // Aggiorna le fasi nello stage4 della configurazione del test
      if (configData && configData.phases) {
        const stage4Index = 3; // Stage 4 è all'indice 3
        if (this.localTest[stage4Index]) {
          this.$set(this.localTest[stage4Index], 'phases', configData.phases);
          this.emitChange();
        }
      }
    },

    onProductSelectionElementSelected(elementType, elementData) {
      console.log('TestEditor: onProductSelectionElementSelected called with:', elementType, elementData);

      if (elementType === 'phase') {
        // Gestisce la selezione di una fase di lavorazione
        this.selectedItem = elementData;
        this.selectedItemType = 'phase';
        this.selectedStageIndex = 3; // Stage 4 (indice 3)
        this.selectedQuestionIndex = null;
        this.selectedAnswerIndex = null;
        this.selectedPhaseIndex = this.localTest[3].phases.findIndex((p) => p.id === elementData.id);
        this.selectedChoiceIndex = null;
        this.updateConfigPanelOffset();
      } else if (elementType === 'choice') {
        // Gestisce la selezione di una scelta
        this.selectedItem = elementData;
        this.selectedItemType = 'choice';
        this.selectedStageIndex = 3; // Stage 4 (indice 3)
        this.selectedQuestionIndex = null;
        this.selectedAnswerIndex = null;

        // Trova la fase e la scelta corrispondenti
        const stage4 = this.localTest[3];
        if (stage4 && stage4.phases) {
          for (let phaseIndex = 0; phaseIndex < stage4.phases.length; phaseIndex++) {
            const phase = stage4.phases[phaseIndex];
            if (phase && phase.choices) {
              const choiceIndex = phase.choices.findIndex((c) => c.id === elementData.id);
              if (choiceIndex !== -1) {
                this.selectedPhaseIndex = phaseIndex;
                this.selectedChoiceIndex = choiceIndex;
                break;
              }
            }
          }
        }
      }
    },

    updateConfigPanelOffset() {
      // Reset immediato per evitare che il vecchio offset influenzi il layout
      // durante la transizione dello stepper
      this.configPanelOffset = 0;
      if (this.offsetTimer) {
        clearTimeout(this.offsetTimer);
        this.offsetTimer = null;
      }
      this.$nextTick(() => {
        if (!this.selectedItem || this.mode !== 'edit') {
          return;
        }
        // Aspetta che la transizione dello stepper (expand/collapse) sia completata
        this.offsetTimer = setTimeout(() => {
          this.offsetTimer = null;
          const row = this.$refs.editorRow && (this.$refs.editorRow.$el || this.$refs.editorRow);
          if (!row) return;
          const selectedEl = row.querySelector('.question-selected, .phase-selected');
          if (!selectedEl) {
            this.configPanelOffset = 0;
            return;
          }
          const { configPanel } = this.$refs;
          if (!configPanel) return;
          const selectedRect = selectedEl.getBoundingClientRect();
          const panelRect = configPanel.getBoundingClientRect();
          this.configPanelOffset = Math.max(0, selectedRect.top - panelRect.top);
        }, 350);
      });
    },
  },
};
</script>

<style scoped>
.component-testeditor {
  height: 100%;
}

.editor-header {
  padding: 0;
}

.editor-content {
  flex: 1;
}

.preview-container {
  height: 100%;
}

.config-panel {
  height: 100%;
  transition: margin-top 0.3s ease;
}

.grey.lighten-5 {
  border-left: 1px solid #e0e0e0;
}

.v-btn-toggle {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}
</style>
